import { roundFractionalToInt } from '@betgames/bg-tools';
import { BetType } from '@bg-shared/enums/BetType';
import {
    IBettingLimits,
    IBettingLimitsParams,
    IBettingLimitsPayload,
    IBettingLimitsResponse,
    IBettingLimitsResponseFormatted,
    ILimitSuccessResponseItem,
    ILimitInvalidResponseItem,
} from './interfaces';

enum LimitOperation {
    MIN = 'min',
    MAX = 'max',
}

interface ILimitOperations {
    min: LimitOperation;
    max: LimitOperation;
}

export default class BettingLimitsFormatter {
    private formatLimits(
        data: ILimitSuccessResponseItem[],
        operations: ILimitOperations,
    ): IBettingLimits | null {
        if (!data.length) {
            return null;
        }

        return {
            min: Math[operations.min](...data.map((item) => item.min)),
            max: Math[operations.max](...data.map((item) => item.max)),
        };
    }

    private formatLimitsMapByOptionId<
        Item extends ILimitSuccessResponseItem | ILimitInvalidResponseItem,
    >(options: IBettingLimitsParams['options'], limits: Item[]) {
        return options.reduce<Record<number, Item>>((result, option, index) => {
            const limitsItem = limits.find((item) => item.betNum === index + 1);

            if (limitsItem) {
                result[option.id] = limitsItem;
            }

            return result;
        }, {});
    }

    public get defaultValidation(): IBettingLimitsResponseFormatted {
        return {
            limitsBoundaries: null,
            limitsTotalRange: null,
            validLimitsByOptionId: {},
            invalidLimitsByOptionId: {},
            items: [],
            invalidLimitsIndexes: [],
        };
    }

    public formatPayload = (params: IBettingLimitsParams): IBettingLimitsPayload => {
        return {
            bets: params.options.map((option, index) => ({
                runId: option.runId,
                oddId: option.id,
                oddValue: roundFractionalToInt(option.value, 2),
                ...(params.type === BetType.SINGLE && { betNum: index + 1 }),
                ...(option.items && { items: option.items }),
                ...(option.runRoundId && { runRoundId: option.runRoundId }),
            })),
            type: params.type,
        };
    };

    public formatResponse = (
        options: IBettingLimitsParams['options'],
        response: IBettingLimitsResponse,
    ): IBettingLimitsResponseFormatted => {
        const { valid, invalid } = response.reduce<{
            valid: ILimitSuccessResponseItem[];
            invalid: ILimitInvalidResponseItem[];
        }>(
            (result, item) => {
                if ('error' in item) {
                    result.invalid.push(item);
                } else {
                    result.valid.push(item);
                }
                return result;
            },
            { valid: [], invalid: [] },
        );

        return {
            limitsBoundaries: this.formatLimits(valid, {
                min: LimitOperation.MAX,
                max: LimitOperation.MIN,
            }),
            limitsTotalRange: this.formatLimits(valid, {
                min: LimitOperation.MIN,
                max: LimitOperation.MAX,
            }),
            validLimitsByOptionId: this.formatLimitsMapByOptionId(options, valid),
            invalidLimitsByOptionId: this.formatLimitsMapByOptionId(options, invalid),
            invalidLimitsIndexes: invalid.map((item) => item.betNum - 1),
            items: response,
        };
    };
}

export const bettingLimitsFormatter = new BettingLimitsFormatter();
